import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  InputSignal,
  Signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ModalController,
  IonIcon,
  IonRippleEffect,
} from '@ionic/angular/standalone';
import {
  register as registerSwiper,
  SwiperContainer,
} from 'swiper/element/bundle';
import { Swiper } from 'swiper/types';
import { SwiperOptions } from 'swiper/types';
import { AlfredClientConfig } from '../../alfred-client.config';
import { Image } from '../../models/questions/image.model';
import { ImageModalComponent } from './image-modal/image-modal.component';
import { addIcons } from 'ionicons';
import {
  openOutline,
  chevronBackOutline,
  chevronForwardOutline,
} from 'ionicons/icons';

export enum ImageView {
  SINGLE = 'SINGLE',
  SLIDER = 'SLIDER',
}

/**
 * A component that displays an image when an image URL is passed to this
 * component as an input. The image will be opened in a modal when the user
 * clicks on it.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  imports: [IonIcon, IonRippleEffect],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ImageViewerComponent implements AfterViewInit {
  private readonly modalController: ModalController = inject(ModalController);
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);

  /**
   * HTML template injection of {@see ImageView}.
   */
  public readonly ImageView: typeof ImageView = ImageView;

  public readonly swiperRef: Signal<ElementRef<SwiperContainer>> =
    viewChild.required<ElementRef<SwiperContainer>>('swiper');

  /**
   * Images to show limited by the imageLimit.
   */
  public readonly images: InputSignal<Image[]> = input<Image[]>([]);

  /**
   * How to view images.
   */
  public readonly imageView: InputSignal<ImageView> = input<ImageView>(
    ImageView.SINGLE,
  );

  constructor() {
    addIcons({ openOutline, chevronBackOutline, chevronForwardOutline });
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    if (this.imageView() === 'SLIDER') {
      registerSwiper();
      this.initSwiper();
    }
  }

  /**
   * Return the URL of the image.
   */
  public url(image: Image): string {
    const url: string = image.url;
    return url.match(/^http[s]?:\/\//) ? url : this.config.alfredApiUrl + url;
  }

  /**
   * Present a modal to show the image.
   */
  public presentImageModal(image: Image): void {
    this.modalController
      .create({
        component: ImageModalComponent,
        componentProps: { imageUrl: this.url(image) },
        cssClass: 'lib-alfred-client-modal-image',
      })
      .then((modal) => modal.present());
  }

  /**
   * Initialize the Slider.
   */
  // eslint-disable-next-line max-lines-per-function
  public initSwiper(): void {
    const swiperContainer: SwiperContainer = this.swiperRef().nativeElement;
    const imagesLength: number = this.images().length;

    const swiperOptions: SwiperOptions = {
      autoHeight: true,
      grabCursor: true,
      speed: 400,
      slidesPerView: 1,
      spaceBetween: 16,
      navigation: {
        nextEl: '.button-next',
        prevEl: '.button-prev',
      },
      pagination: {
        clickable: true,
        type: 'custom',
        renderCustom: function (
          swiper: Swiper,
          current: number,
          total: number,
        ) {
          if (swiper.currentBreakpoint !== '720') {
            return `Afbeelding ${current} van de ${total}`;
          }

          return `Afbeelding ${current}, ${current + 1} en ${
            current + 2
          } van de ${imagesLength}`;
        },
      },
      breakpoints: {
        720: {
          slidesPerView: this.images().length < 3 ? this.images().length : 3,
        },
      },
      injectStyles: [
        `
        .swiper-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.5;
          line-height: 1.25rem;
          font-size: .75rem;
          height: 45px;
        }

        .swiper-button-next, .swiper-button-prev {
          display:none;
        }
        `,
      ],
    };

    if (swiperContainer && swiperOptions) {
      Object.assign(swiperContainer, swiperOptions);
      swiperContainer.initialize();
    }
  }
}
